import { useState } from 'react';
import type { JSX } from 'react';

import { SectionBlock } from 'shared/ui';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { useGetIntegrationConfigQuery, useUpdateIntegrationConfigMutation } from '../api/integrations';
import { formatJson, getAtPath, isSameJson, setAtPath } from '../lib/json';
import type { ConfigField, ConfigLayer, IntegrationDetail, JsonObject } from '../model/integration';

const MASTER_LAYER = 'master';
const INHERIT = '__inherit__';

interface SubProject {
  id: number;
  name: string;
}

const formatInherited = (value: unknown): string =>
  value === undefined ? 'sin valor' : typeof value === 'string' ? value : JSON.stringify(value);

interface LeafControlProps {
  field: Exclude<ConfigField, { kind: 'object' }>;
  own: unknown;
  inherited: unknown;
  disabled: boolean;
  onChange: (value: unknown) => void;
  onJsonValidity: (isValid: boolean) => void;
}

// undefined means "not set in this layer": the key is left out and the layer below decides
const LeafControl = ({ field, own, inherited, disabled, onChange, onJsonValidity }: LeafControlProps): JSX.Element => {
  const [jsonText, setJsonText] = useState(own === undefined ? '' : formatJson(own));
  const [jsonError, setJsonError] = useState(false);

  switch (field.kind) {
    case 'boolean':
      return (
        <Switch
          checked={Boolean(own ?? inherited)}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked)}
          aria-label={field.key}
        />
      );
    case 'enum':
      return (
        <Select
          value={typeof own === 'string' ? own : INHERIT}
          disabled={disabled}
          onValueChange={(v) => onChange(v === INHERIT ? undefined : v)}
        >
          <SelectTrigger size="sm" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={INHERIT}>Heredar ({formatInherited(inherited)})</SelectItem>
            {field.options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'number':
    case 'string':
      return (
        <Input
          type={field.kind === 'number' ? 'number' : 'text'}
          className="h-8 w-48"
          value={own === undefined ? '' : String(own)}
          placeholder={formatInherited(inherited)}
          disabled={disabled}
          onChange={(event) => {
            const text = event.target.value;
            onChange(text === '' ? undefined : field.kind === 'number' ? Number(text) : text);
          }}
        />
      );
    case 'json':
      return (
        <div className="flex w-full max-w-md flex-col gap-1">
          <Textarea
            className="min-h-16 font-mono text-xs"
            value={jsonText}
            placeholder={formatJson(inherited)}
            disabled={disabled}
            onChange={(event) => {
              const text = event.target.value;
              setJsonText(text);
              if (text.trim() === '') {
                setJsonError(false);
                onJsonValidity(true);
                onChange(undefined);
                return;
              }
              try {
                onChange(JSON.parse(text));
                setJsonError(false);
                onJsonValidity(true);
              } catch {
                setJsonError(true);
                onJsonValidity(false);
              }
            }}
          />
          {jsonError ? <span className="text-xs text-error">JSON inválido</span> : null}
        </div>
      );
    default:
      return <span />;
  }
};

interface FieldsProps {
  fields: ConfigField[];
  path: string[];
  draft: JsonObject;
  inherited: JsonObject | null;
  /** Top-level keys this layer can't set: their controls are off, but a stored value can still be removed */
  lockedKeys: string[];
  /*
  Remount counters, so a control's local text starts over when its key changes from outside: per key for "Heredar",
  and one for every control on "Descartar"
  */
  resets: Record<string, number>;
  discards: number;
  onChange: (path: string[], value: unknown) => void;
  onInherit: (path: string[]) => void;
  onJsonValidity: (key: string, isValid: boolean) => void;
}

const ConfigFields = ({
  fields,
  path,
  draft,
  inherited,
  lockedKeys,
  resets,
  discards,
  onChange,
  onInherit,
  onJsonValidity,
}: FieldsProps): JSX.Element => (
  <div className="flex flex-col divide-y divide-border-subtle">
    {fields.map((field) => {
      const fieldPath = [...path, field.key];
      const pathKey = fieldPath.join('.');
      const isLocked = lockedKeys.includes(fieldPath[0] ?? '');
      if (field.kind === 'object') {
        return (
          <div key={pathKey} className="flex flex-col gap-1 py-3">
            <span className="font-mono text-sm font-medium">{field.key}</span>
            {field.description ? <span className="text-xs text-text-muted">{field.description}</span> : null}
            {isLocked && path.length === 0 ? (
              <span className="text-xs text-text-muted">
                Solo se configura para toda la empresa: los procesos no leen esta sección por subproyecto.
              </span>
            ) : null}
            <div className="ml-2 border-l border-border-subtle pl-4">
              <ConfigFields
                fields={field.fields}
                path={fieldPath}
                draft={draft}
                inherited={inherited}
                lockedKeys={lockedKeys}
                resets={resets}
                discards={discards}
                onChange={onChange}
                onInherit={onInherit}
                onJsonValidity={onJsonValidity}
              />
            </div>
          </div>
        );
      }
      const own = getAtPath(draft, fieldPath);
      const isSet = own !== undefined;
      return (
        <div key={pathKey} className="flex flex-wrap items-center justify-between gap-3 py-2">
          <div className="flex min-w-60 flex-1 flex-col gap-0.5">
            <span className="font-mono text-xs">{field.key}</span>
            {field.description ? <span className="text-xs text-text-muted">{field.description}</span> : null}
          </div>
          <div className="flex items-center gap-2">
            <LeafControl
              key={`${discards}-${resets[pathKey] ?? 0}`}
              field={field}
              own={own}
              inherited={getAtPath(inherited, fieldPath)}
              disabled={isLocked}
              onChange={(value) => onChange(fieldPath, value)}
              onJsonValidity={(isValid) => onJsonValidity(pathKey, isValid)}
            />
            {isSet ? (
              <>
                <Badge variant="secondary">Definido aquí</Badge>
                <Button size="sm" variant="ghost" onClick={() => onInherit(fieldPath)}>
                  Heredar
                </Button>
              </>
            ) : (
              <span className="w-36 text-xs text-text-muted">Heredado</span>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

interface LayerEditorProps {
  integrationId: number;
  subProjectId: number | null;
  fields: ConfigField[];
  layer: ConfigLayer | null;
  inherited: JsonObject | null;
  lockedKeys: string[];
  notify: (message: string) => void;
}

// Keyed by layer from the parent, so switching layers starts from what that layer stores
const LayerEditor = ({
  integrationId,
  subProjectId,
  fields,
  layer,
  inherited,
  lockedKeys,
  notify,
}: LayerEditorProps): JSX.Element => {
  const stored = layer?.value ?? {};
  const [draft, setDraft] = useState<JsonObject>(stored);
  const [invalidJsonKeys, setInvalidJsonKeys] = useState<string[]>([]);
  const [resets, setResets] = useState<Record<string, number>>({});
  const [discards, setDiscards] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [updateConfig, { isLoading }] = useUpdateIntegrationConfigMutation();

  const isDirty = !isSameJson(draft, stored);
  // The API deletes a layer saved with no keys, so it stops counting as an override
  const deletesLayer = layer?.ruleId != null && Object.keys(draft).length === 0;

  const inherit = (path: string[]): void => {
    const pathKey = path.join('.');
    setDraft((previous) => setAtPath(previous, path, undefined));
    setInvalidJsonKeys((previous) => previous.filter((key) => key !== pathKey));
    setResets((previous) => ({ ...previous, [pathKey]: (previous[pathKey] ?? 0) + 1 }));
  };

  const discard = (): void => {
    setDraft(stored);
    setInvalidJsonKeys([]);
    setDiscards((count) => count + 1);
  };

  const save = async (): Promise<void> => {
    setError(null);
    try {
      await updateConfig({ id: integrationId, body: { subProjectId, value: draft } }).unwrap();
      notify(deletesLayer ? 'Capa eliminada' : 'Configuración guardada');
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo guardar la configuración'));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {layer?.error ? (
        <p className="m-0 rounded-lg border border-border-subtle px-3 py-2 text-xs text-error">
          Hoy esta capa no pasa el esquema, así que los procesos de la empresa fallan: {layer.error}
        </p>
      ) : null}
      <ConfigFields
        fields={fields}
        path={[]}
        draft={draft}
        inherited={inherited}
        lockedKeys={lockedKeys}
        resets={resets}
        discards={discards}
        onChange={(path, value) => setDraft((previous) => setAtPath(previous, path, value))}
        onInherit={inherit}
        onJsonValidity={(key, isValid) =>
          setInvalidJsonKeys((previous) => [...previous.filter((k) => k !== key), ...(isValid ? [] : [key])])
        }
      />
      <details>
        <summary className="cursor-pointer text-xs text-text-muted">Valor que se guarda en la regla</summary>
        <pre className="mt-1 overflow-auto rounded bg-bg-surface-raised p-2 text-[11px]">{formatJson(draft)}</pre>
      </details>
      {error ? <p className="m-0 text-sm text-error break-words">{error}</p> : null}
      <div className="flex items-center justify-end gap-2">
        {deletesLayer ? (
          <span className="text-xs text-text-muted">Sin claves definidas: al guardar se elimina esta capa.</span>
        ) : null}
        <Button size="sm" variant="outline" disabled={!isDirty || isLoading} onClick={discard}>
          Descartar
        </Button>
        <Button
          size="sm"
          variant={deletesLayer ? 'destructive' : 'default'}
          disabled={!isDirty || isLoading || invalidJsonKeys.length > 0}
          onClick={save}
        >
          {isLoading ? 'Guardando…' : deletesLayer ? 'Eliminar capa' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
};

interface ConfigSectionProps {
  integration: IntegrationDetail;
  subProjects: SubProject[];
  notify: (message: string) => void;
}

/*
Editor of the provider's config business rule. It has layers: the master's applies to the whole company and each
subproject may override it. A layer stores only the keys it sets; every other key comes from the layer below (the
defaults for the master, the master for a subproject), which is what "Heredar" goes back to.
*/
const ConfigSection = ({ integration, subProjects, notify }: ConfigSectionProps): JSX.Element => {
  const [selectedLayer, setSelectedLayer] = useState(MASTER_LAYER);
  const { data: config, isLoading, error } = useGetIntegrationConfigQuery(integration.id);

  const subProjectId = selectedLayer === MASTER_LAYER ? null : Number(selectedLayer);
  const layer =
    subProjectId === null
      ? config?.master ?? null
      : config?.subProjects.find(({ projectId }) => projectId === subProjectId) ?? null;
  const inherited = subProjectId === null ? config?.defaults ?? null : config?.master.resolved ?? null;
  const overriddenIds = new Set(config?.subProjects.map(({ projectId }) => projectId));

  // An override a run reads must be reachable even when its project is not among the master's subprojects
  const knownIds = new Set(subProjects.map(({ id }) => id));
  const layerOptions = [
    ...subProjects,
    ...[...overriddenIds]
      .filter((id) => !knownIds.has(id))
      .map((id) => ({ id, name: `Proyecto #${id} (no aparece en el master)` })),
  ];

  return (
    <SectionBlock
      title={config ? `Configuración · ${config.ruleName}` : 'Configuración'}
      action={
        layerOptions.length > 0 ? (
          <Select value={selectedLayer} onValueChange={setSelectedLayer}>
            <SelectTrigger size="sm" className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={MASTER_LAYER}>Toda la empresa</SelectItem>
              {layerOptions.map(({ id, name }) => (
                <SelectItem key={id} value={id.toString()}>
                  {name}
                  {overriddenIds.has(id) ? ' · con override' : ''}
                  {config?.subProjects.find(({ projectId }) => projectId === id)?.error ? ' · con error' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null
      }
    >
      {isLoading ? <p className="m-0 text-sm text-text-muted">Cargando…</p> : null}
      {error ? (
        <p className="m-0 text-sm text-error">{extractErrorMessage(error, 'No se pudo cargar la configuración')}</p>
      ) : null}
      {config ? (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-xs text-text-muted">
            {subProjectId === null
              ? 'Aplica a toda la empresa. Lo que no definas aquí usa el valor por defecto.'
              : 'Override de este subproyecto. Lo que no definas aquí usa el valor de toda la empresa.'}
          </p>
          <LayerEditor
            key={`${selectedLayer}-${layer?.ruleId ?? 'new'}`}
            integrationId={integration.id}
            subProjectId={subProjectId}
            fields={config.fields}
            layer={layer}
            inherited={inherited}
            lockedKeys={subProjectId === null ? [] : config.masterOnlyKeys}
            notify={notify}
          />
        </div>
      ) : null}
    </SectionBlock>
  );
};

export default ConfigSection;
